<template>
  <div class="user-notes">
    <!-- Notes Table -->
    <NewBaseTable
      v-if="userId"
      ref="notesTable"
      :fields="noteFields"
      :filter-settings="{}"
      :url="`/api/users/${userId}/notes/list`"
      :disable-header="false"
      :show-pagination="false"
      :is-frontend-pagination="false"
      :first-search="true"
      :hide-search="true"
      :item-per-page="5"
      :current-page="1"
      :http-client="$httpSupport"
      :inject-actions="{ modifyParams: modifyParams }"
      method="get"
      class="notes-table"
      @beforeShowingData="handleBeforeShowingData"
      @afterShowingData="handleAfterShowingData"
    >
      <template #tableHeader>
        <h3 class="mb-0">User Notes</h3>
      </template>
      
      <template #cell(admin)="data">
        <UserBadgeName 
          :name="getAdminName(data.row)"
          :color="getAdminColor(data.row)"
        />
      </template>

      <template #cell(text)="data">
        <div 
          v-html="parseNoteLinks(truncateText(data.row.text, 255))"
          @click="handleLinkClick"
          class="note-text-content"
        ></div>
      </template>

      <template #cell(status)="data">
        <div class="d-flex justify-content-center">
          <BButton
            variant="link"
            size="sm"
            :class="{'text-warning': data.row.status === 1}"
            @click="handleToggleStatus(data.row, 1)"
            v-b-tooltip.hover.top="data.row.status === 1 ? 'Unpin' : 'Pin'"
          >
            <FeatherIcon 
              :icon="data.row.status === 1 ? 'MapPinIcon' : 'MapPinIcon'"
              :fill="data.row.status === 1 ? 'currentColor' : 'none'"
              size="18"
            />
          </BButton>
          <BButton
            variant="link"
            size="sm"
            :class="{'text-danger': data.row.status === 2}"
            @click="handleToggleStatus(data.row, 2)"
            v-b-tooltip.hover.top="data.row.status === 2 ? 'Remove Alert' : 'Mark as Alert'"
          >
            <FeatherIcon 
              :icon="data.row.status === 2 ? 'AlertCircleIcon' : 'AlertCircleIcon'"
              :fill="data.row.status === 2 ? 'currentColor' : 'none'"
              size="18"
            />
          </BButton>
        </div>
      </template>

      <template #cell(review)="data">
        <BBadge 
          v-if="data.row.review_id"
          variant="danger"
        >
          Review
        </BBadge>
        <span v-else class="text-muted">-</span>
      </template>

      <template #cell(created_at)="data">
        <small>{{ timeAgo(data.row.created_at, new Date(), true) }}</small>
      </template>

      <template #additionalFooter>
        <!-- Action Buttons -->
        <div class="d-flex justify-content-between align-items-center">
          <BButton
            variant="primary"
            size="sm"
            @click="showAddNoteModal = true"
          >
            Add Note
          </BButton>
          <BButton
            v-if="pagination.total > 5"
            variant="outline-primary"
            size="sm"
            @click="openAllNotesModal"
          >
            All ({{ pagination.total }})
          </BButton>
        </div>
      </template>
    </NewBaseTable>

    <!-- Empty State -->
    <div v-else class="card">
      <b-card-header>
        <h3 class="mb-0">User Notes</h3>
      </b-card-header>
      <b-card-body>
        <div class="text-center text-muted py-3">
          <FeatherIcon icon="MessageSquareIcon" size="24" class="mb-2" />
          <p class="mb-0">No notes yet</p>
        </div>
        
        <!-- Action Buttons -->
        <div class="mt-3 pt-3 border-top d-flex justify-content-between align-items-center">
          <BButton
            variant="primary"
            size="sm"
            @click="showAddNoteModal = true"
          >
            Add Note
          </BButton>
          <BButton
            v-if="pagination.total > 5"
            variant="outline-primary"
            size="sm"
            @click="openAllNotesModal"
          >
            All ({{ pagination.total }})
          </BButton>
        </div>
      </b-card-body>
    </div>
  
      <!-- Add Note Modal -->
      <BModal
        v-model="showAddNoteModal"
        title="Add New Note"
        centered
        @hidden="resetAddNoteForm"
      >
        <BForm @submit.prevent="handleAddNote">
          <BFormTextarea
            v-model="newNote"
            placeholder="Type note..."
            rows="4"
            no-resize
            maxlength="255"
            :disabled="isLoading"
            autofocus
          />
          <div class="d-flex justify-content-between align-items-center mt-2">
            <small class="text-muted">{{ newNote.length }}/255 characters</small>
            <small v-if="newNote.length > 255" class="text-danger">
              Character limit exceeded
            </small>
          </div>
        </BForm>
  
        <template #modal-footer>
          <BButton
            variant="secondary"
            @click="showAddNoteModal = false"
            :disabled="isLoading"
          >
            Cancel
          </BButton>
          <BButton
            variant="primary"
            @click="handleAddNote"
            :disabled="!newNote.trim() || isLoading || newNote.length > 255"
          >
            <BSpinner v-if="isLoading" small class="mr-1" />
            Save Note
          </BButton>
        </template>
      </BModal>
  
    <!-- All Notes Modal -->
    <BModal
      v-model="showAllNotesModal"
      title="All User Notes"
      size="lg"
      scrollable
      hide-footer
    >
      <div v-if="isLoadingAllNotes" class="text-center py-5">
        <BSpinner variant="primary" />
        <p class="mt-2 text-muted">Loading all notes...</p>
      </div>
      <NewBaseTable
        v-else
        ref="allNotesTable"
        :fields="noteFields"
        :filter-settings="{}"
        :url="`/api/users/${userId}/notes/list`"
        :disable-header="true"
        :show-pagination="false"
        :is-frontend-pagination="false"
        :first-search="true"
        :hide-search="true"
        :item-per-page="999"
        :http-client="$httpSupport"
        :inject-actions="{ modifyParams: modifyParamsAll }"
        method="get"
        class="all-notes-table"
        @beforeShowingData="handleBeforeShowingDataAll"
      >
        <template #cell(admin)="data">
          <UserBadgeName 
            :name="getAdminName(data.row)"
            :color="getAdminColor(data.row)"
          />
        </template>
  
        <template #cell(text)="data">
          <div 
            v-html="parseNoteLinks(truncateText(data.row.text, 255))"
            @click="handleLinkClick"
            class="note-text-content"
          ></div>
        </template>

        <template #cell(status)="data">
          <div class="d-flex justify-content-center">
            <BButton
              variant="link"
              size="sm"
              :class="{'text-warning': data.row.status === 1}"
              @click="handleToggleStatus(data.row, 1)"
              v-b-tooltip.hover.top="data.row.status === 1 ? 'Unpin' : 'Pin'"
            >
              <FeatherIcon 
                :icon="data.row.status === 1 ? 'MapPinIcon' : 'MapPinIcon'"
                :fill="data.row.status === 1 ? 'currentColor' : 'none'"
                size="18"
              />
            </BButton>
            <BButton
              variant="link"
              size="sm"
              :class="{'text-danger': data.row.status === 2}"
              @click="handleToggleStatus(data.row, 2)"
              v-b-tooltip.hover.top="data.row.status === 2 ? 'Remove Alert' : 'Mark as Alert'"
            >
              <FeatherIcon 
                :icon="data.row.status === 2 ? 'AlertCircleIcon' : 'AlertCircleIcon'"
                :fill="data.row.status === 2 ? 'currentColor' : 'none'"
                size="18"
              />
            </BButton>
          </div>
        </template>
  
        <template #cell(review)="data">
          <BBadge 
            v-if="data.row.review_id"
            variant="danger"
          >
            Review
          </BBadge>
          <span v-else class="text-muted">-</span>
        </template>
  
        <template #cell(created_at)="data">
          <small>{{ timeAgo(data.row.created_at, new Date(), true) }}</small>
        </template>
      </NewBaseTable>
    </BModal>
    </div>
  </template>
  
  <script>
  import { BButton, BFormTextarea, BForm, BSpinner, BCardHeader, BCardBody, BModal, BBadge } from 'bootstrap-vue'
  import UserBadgeName from '@/views/users/UserBadgeName.vue'
  import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
  import { timeAgo, parseNoteLinks } from '@/utils/helpers'
  import NewBaseTable from '@/main/custom-components/BaseTable/NewBaseTable.vue'
  
  export default {
    name: 'UserNotes',
    components: {
      BButton,
      BFormTextarea,
      BForm,
      BSpinner,
      BCardHeader,
      BCardBody,
      BModal,
      BBadge,
      NewBaseTable,
      UserBadgeName,
      FeatherIcon
    },
    props: {
      userId: {
        type: [String, Number],
        required: true
      }
    },
  data() {
    return {
      displayedNotesData: [],
      newNote: '',
      isLoading: false,
      isLoadingAllNotes: false,
      showAllNotesModal: false,
      showAddNoteModal: false,
      pagination: {
        current_page: 1,
        per_page: 5,
        total: 0,
        last_page: 1
      },
      noteFields: [
        { key: 'admin', label: 'Admin', thStyle: { width: '200px' }, sortable: false },
        { key: 'text', label: 'Note', thStyle: { minWidth: '300px' }, sortable: false },
        { key: 'status', label: 'Status', thStyle: { width: '100px', textAlign: 'center' }, class: 'text-center', sortable: false },
        { key: 'review', label: 'Review', thStyle: { width: '100px' }, class: 'text-center', sortable: false },
        { key: 'created_at', label: 'Time', thStyle: { width: '150px' }, sortable: false }
      ]
    }
  },
  computed: {
    displayedNotes() {
      return this.displayedNotesData
    }
  },
    methods: {
      parseNoteLinks,
      timeAgo,
      
      truncateText(text, maxLength) {
        if (!text) return ''
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
      },

      getAdminName(note) {
        if (note.author && note.author.name) {
          return note.author.name
        }
        return 'System'
      },

      getAdminColor(note) {
        if (note.author && note.author.hex_color) {
          return note.author.hex_color
        }
        return '#6c757d' // Default gray color
      },

      // Parameter modification functions for NewBaseTable
      modifyParams(params) {
        // Completely override parameters for main notes table
        return {
          page: 1,
          per_page: 5
        }
      },

      modifyParamsAll(params) {
        // Completely override parameters for all notes modal
        return {
          all: 'true'
        }
      },

      // NewBaseTable event handlers
      handleBeforeShowingData({ filterValues }) {
        // Clear any existing parameters and set only what the backend expects
        const cleanFilterValues = {
          page: 1,
          per_page: 5
        }
        return {
          filterValues: cleanFilterValues,
          url: `/api/users/${this.userId}/notes/list`,
          httpClient: this.$httpSupport
        }
      },

      handleAfterShowingData(responseData) {
        // Store the data for display
        this.displayedNotesData = responseData.data || []
        const paginationData = responseData.pagination || responseData
        this.pagination.current_page = paginationData.current_page || 1
        this.pagination.per_page = paginationData.per_page || 5
        this.pagination.total = paginationData.total || 0
        this.pagination.last_page = paginationData.last_page || 1
      },

      handleBeforeShowingDataAll({ filterValues }) {
        // Clear any existing parameters and set only what the backend expects for all notes
        const cleanFilterValues = {
          all: 'true'
        }
        return {
          filterValues: cleanFilterValues,
          url: `/api/users/${this.userId}/notes/list`,
          httpClient: this.$httpSupport
        }
      },

  
      async handleAddNote() {
        if (!this.newNote.trim() || this.newNote.length > 255) {
          return
        }

        try {
          this.isLoading = true
          const noteText = this.newNote.trim()
          
          // Attempt to create the note
          try {
            await this.$httpSupport.post(`/api/users/${this.userId}/notes`, {
              text: noteText
            })
          } catch (createError) {
            // Check if the error is the known backend issue with getUuid() method
            if (createError.response && 
                createError.response.data && 
                createError.response.data.message && 
                createError.response.data.message.includes('getUuid()')) {
              // This is a known backend issue - the note is actually created but response fails
              // We'll continue and refresh the list to show the new note
              console.warn('Known backend issue with note creation response, but note was likely created')
            } else {
              // Re-throw if it's a different error
              throw createError
            }
          }
          
          this.newNote = ''
          this.showAddNoteModal = false
          // Refresh the main notes table
          if (this.$refs.notesTable) {
            this.$refs.notesTable.search()
          }
          this.alertSuccess('Note added successfully')
        } catch (error) {
          console.error('Error adding note:', error)
          this.alertError('Failed to add note')
        } finally {
          this.isLoading = false
        }
      },
  
      resetAddNoteForm() {
        this.newNote = ''
        this.isLoading = false
      },
  
      openAllNotesModal() {
        this.showAllNotesModal = true
      },
  
      async handleToggleStatus(note, targetStatus) {
        try {
          const newStatus = note.status === targetStatus ? null : targetStatus

          const noteId = note.id
          if (!noteId) {
            console.error('Note ID not found:', note)
            this.alertError('Note ID not found')
            return
          }

          await this.$httpSupport.patch(
            `/api/users/${this.userId}/notes/${noteId}/status`,
            { status: newStatus }
          )

          if (this.$refs.notesTable) {
            this.$refs.notesTable.search()
          }

          if (this.showAllNotesModal && this.$refs.allNotesTable) {
            this.$refs.allNotesTable.search()
          }

          const statusText = newStatus 
            ? (newStatus === 1 ? 'pinned' : 'marked as alert')
            : 'status cleared'
          this.alertSuccess(`Note ${statusText}`)
        } catch (error) {
          console.error('Error updating note status:', error)
          this.alertError('Failed to update note status')
        }
      },

      handleLinkClick(event) {
        // Check if the clicked element is a note-link
        const target = event.target
        if (target && target.classList.contains('note-badge')) {
          event.preventDefault()
          
          const route = target.getAttribute('data-route')
          const prefix = target.getAttribute('data-prefix')
          const id = target.getAttribute('data-id')
          
          if (!route) return
          
          // Map prefix to route names
          const routeNameMap = {
            's': 'survey_details',
            'ps': 'user_details',
            'hc': 'hc_user_details',
            'ts': 'ts_user_details',
            'po': 'po_user_details',
            'fs': 'fs_user_details',
            'es': 'es_user_details',
            'o': 'offer_details'
          }
          
          const routeName = routeNameMap[prefix]
          
          if (routeName && id) {
            // Use Vue Router to open in new tab
            const routeData = this.$router.resolve({
              name: routeName,
              params: { 
                userId: parseInt(id),
                surveyId: parseInt(id),
                offerId: parseInt(id)
              }
            })
            window.open(routeData.href, '_blank')
          } else {
            // Fallback to direct navigation
            window.open(route, '_blank')
          }
        }
      }
    }
  }
  </script>
  
  <style lang="scss" scoped>
  .user-notes {
    .notes-table, .all-notes-table {
      ::v-deep {
        .base-table-header {
          .card-header {
            padding-bottom: 0.5rem !important;
          }
        }

        thead {
          th {
            background-color: #f8f8f8;
            border-bottom: 2px solid #e9ecef;
            font-weight: 600;
            font-size: 0.875rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #5e5873;
            padding: 0.75rem;
            
            .dark-layout & {
              background-color: #283046;
              color: #b4b7bd;
              border-bottom-color: #3b4253;
            }

            // Status column header centering
            &:nth-child(3) {
              text-align: center !important;
            }
          }
        }

        tbody {
          tr {
            transition: background-color 0.2s ease;

            &:hover {
              background-color: rgba(115, 103, 240, 0.05);
            }

            td {
              padding: 1rem 0.75rem;
              vertical-align: middle;
              border-bottom: 1px solid #ebe9f1;

              .dark-layout & {
                border-bottom-color: #3b4253;
              }
            }
          }
        }

        .note-text-content {
          line-height: 1.6;
          word-wrap: break-word;
          font-size: 0.95rem;
          color: #5e5873;

          .dark-layout & {
            color: #b4b7bd;
          }

          .note-badge {
            background-color: #7367f0 !important;
            color: white !important;
            font-size: 0.75rem;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            cursor: pointer;
            transition: all 0.2s ease;
            display: inline-block;
            margin: 0 2px;

            &:hover {
              background-color: #5e50ee !important;
              transform: translateY(-1px);
              box-shadow: 0 2px 4px rgba(115, 103, 240, 0.3);
            }
          }
        }

        .avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          
          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }

      }
    }

    .all-notes-table {
      ::v-deep {
        thead {
          th {
            position: sticky;
            top: 0;
            z-index: 10;
          }
        }
      }
    }
  }

  .dark-layout {
    .user-notes {
      .notes-table, .all-notes-table {
        ::v-deep tbody tr:hover {
          background-color: rgba(115, 103, 240, 0.12);
        }
      }
    }
  }
  </style>
  